import { useEffect, useState } from 'react';
import type { ConfirmCardData } from '../components/VoiceConfirmCard';

/**
 * Presentation phases for accessibility announcements and status chrome.
 * Maps capture + command lifecycle into a single UI-facing state.
 */
export type VoiceUiPhase =
  | 'idle'
  | 'requesting-permission'
  | 'listening'
  | 'speech-detected'
  | 'waiting-for-finish'
  | 'finishing'
  | 'transcribing'
  | 'understanding'
  | 'confirmation-required'
  | 'completed';

export type VoiceCaptureUiDetail = {
  phase: VoiceUiPhase;
  silenceEndsAt?: number | null;
  alertMessage?: string | null;
};

const CAPTURE_EVENT = 'voiceCaptureUiState';

export function emitVoiceCaptureUiState(detail: VoiceCaptureUiDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(CAPTURE_EVENT, { detail }));
}

export interface UseVoiceUiPhaseOptions {
  isRecording: boolean;
  isProcessing: boolean;
  confirmCard?: ConfirmCardData | null;
}

export interface UseVoiceUiPhaseResult {
  phase: VoiceUiPhase;
  silenceEndsAt: number | null;
  silenceSecondsRemaining: number | null;
  alertMessage: string | null;
}

/**
 * Combines engine capture UI events with processing / confirmation props.
 */
export function useVoiceUiPhase({
  isRecording,
  isProcessing,
  confirmCard,
}: UseVoiceUiPhaseOptions): UseVoiceUiPhaseResult {
  const [capturePhase, setCapturePhase] = useState<VoiceUiPhase>('idle');
  const [silenceEndsAt, setSilenceEndsAt] = useState<number | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [silenceSecondsRemaining, setSilenceSecondsRemaining] = useState<number | null>(null);
  const [completedFlash, setCompletedFlash] = useState(false);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<VoiceCaptureUiDetail>).detail;
      if (!detail?.phase) return;
      setCapturePhase(detail.phase);
      setSilenceEndsAt(
        detail.phase === 'waiting-for-finish' && detail.silenceEndsAt
          ? detail.silenceEndsAt
          : null
      );
      if (detail.alertMessage) {
        setAlertMessage(detail.alertMessage);
      } else if (detail.phase !== 'idle') {
        setAlertMessage(null);
      }
      if (detail.phase === 'completed') {
        setCompletedFlash(true);
      }
    };
    window.addEventListener(CAPTURE_EVENT, handler as EventListener);
    return () => window.removeEventListener(CAPTURE_EVENT, handler as EventListener);
  }, []);

  useEffect(() => {
    if (!completedFlash) return;
    const timer = window.setTimeout(() => setCompletedFlash(false), 1500);
    return () => window.clearTimeout(timer);
  }, [completedFlash]);

  useEffect(() => {
    if (!isRecording && capturePhase !== 'idle' && capturePhase !== 'finishing' && capturePhase !== 'transcribing') {
      // Recording ended without an explicit terminal capture phase — reset listening chrome.
      if (
        capturePhase === 'listening' ||
        capturePhase === 'speech-detected' ||
        capturePhase === 'waiting-for-finish' ||
        capturePhase === 'requesting-permission'
      ) {
        setCapturePhase('idle');
        setSilenceEndsAt(null);
      }
    }
  }, [isRecording, capturePhase]);

  useEffect(() => {
    if (silenceEndsAt == null) {
      setSilenceSecondsRemaining(null);
      return;
    }
    const tick = () => {
      const remainingMs = silenceEndsAt - Date.now();
      if (remainingMs <= 0) {
        setSilenceSecondsRemaining(0);
        return;
      }
      setSilenceSecondsRemaining(Math.ceil(remainingMs / 1000));
    };
    tick();
    const id = window.setInterval(tick, 200);
    return () => window.clearInterval(id);
  }, [silenceEndsAt]);

  let phase: VoiceUiPhase = 'idle';
  if (confirmCard) {
    phase = 'confirmation-required';
  } else if (completedFlash) {
    phase = 'completed';
  } else if (isProcessing) {
    if (capturePhase === 'transcribing' || capturePhase === 'finishing') {
      phase = capturePhase;
    } else {
      phase = 'understanding';
    }
  } else if (isRecording) {
    if (
      capturePhase === 'requesting-permission' ||
      capturePhase === 'listening' ||
      capturePhase === 'speech-detected' ||
      capturePhase === 'waiting-for-finish' ||
      capturePhase === 'finishing'
    ) {
      phase = capturePhase;
    } else {
      phase = 'listening';
    }
  } else if (
    capturePhase === 'finishing' ||
    capturePhase === 'transcribing'
  ) {
    phase = capturePhase;
  }

  return {
    phase,
    silenceEndsAt,
    silenceSecondsRemaining:
      phase === 'waiting-for-finish' ? silenceSecondsRemaining : null,
    alertMessage,
  };
}
